import { z } from 'zod';
// Re-added 17 MAY 2026 · `ActivityWeeksResponseSchema` (value) + `ActivityWeek`
// (type) consumed by `getActivity` method below (CF-D-1 endpoint wrapper).
// slot_vocabulary_navbar removed них during VN verify chain — premature
// removal · imports needed.
import {
  ProgressSchema,
  TicketProgressSchema,
  SprintProgressSchema,
  HomeworkProgressSchema,
  OnboardingFlagsSchema,
  PomodoroStateSchema,
  CockpitStateSchema,
  FlashcardStateSchema,
  AchievementsStateSchema,
  CapstoneStateSchema,
  ProgressLastSchema,
  ProgressEventSchema,
  QuizProgressSchema,
  ActivityWeeksResponseSchema,
  type Progress,
  type TicketProgress,
  type SprintProgress,
  type HomeworkProgress,
  type OnboardingFlags,
  type PomodoroState,
  type CockpitState,
  type FlashcardState,
  type AchievementsState,
  type CapstoneState,
  type CapstoneBlockId,
  type ProgressLast,
  type SkillProgress,
  type ActivityWeek,
  type ProgressEvent,
  type QuizProgress,
} from '@shared/schemas';
import { apiFetch, type ApiFetchOptions } from '../client';

const ProgressResponse = z.object({ progress: ProgressSchema });
const SkillsResponse = z.object({ skills: z.record(z.string(), z.unknown()) });
// CtflReadiness · server returns `{chapters: Record<chapterId, fraction0to1>}`
// per `supabase/functions/progress/index.ts` GET /progress/ctfl-readiness.
// Previously typed as array (parity drift since Hono era, see migration log
// SM.4b note) · fixed FS.2 to match server truth.
const ChaptersResponse = z.object({ chapters: z.record(z.string(), z.number()) });

const TicketEcho = z.object({
  ticketId: z.string(),
  progress: TicketProgressSchema,
  etag: z.string(),
});
const SprintEcho = z.object({
  sprintId: z.string(),
  progress: SprintProgressSchema,
  etag: z.string(),
});
const HomeworkEcho = z.object({
  hwId: z.string(),
  progress: HomeworkProgressSchema,
  etag: z.string(),
});
const LastEcho = z.object({ last: ProgressLastSchema, etag: z.string() });
const OnboardingEcho = z.object({ onboarding: OnboardingFlagsSchema, etag: z.string() });
const PomodoroEcho = z.object({ pomodoro: PomodoroStateSchema, etag: z.string() });
const CockpitEcho = z.object({ cockpit: CockpitStateSchema, etag: z.string() });
const FlashcardEcho = z.object({
  termId: z.string(),
  flashcard: FlashcardStateSchema,
  etag: z.string(),
});
const AchievementsEcho = z.object({ achievements: AchievementsStateSchema, etag: z.string() });
const CapstoneEcho = z.object({
  capstone: z.object({ 'block-1': CapstoneStateSchema.optional() }),
  etag: z.string(),
});
const CapstoneGenericEcho = z.object({
  blockId: z.string(),
  capstone: CapstoneStateSchema,
  etag: z.string(),
});
const EventsEcho = z.object({
  events: z.array(ProgressEventSchema),
  etag: z.string(),
});
const QuizEcho = z.object({
  bankId: z.string(),
  progress: QuizProgressSchema,
  etag: z.string(),
});

interface PatchOpts {
  branchUpdatedAt?: string;
  signal?: AbortSignal;
}

function withBranch(opts: PatchOpts | undefined): ApiFetchOptions {
  return {
    branchUpdatedAt: opts?.branchUpdatedAt,
    signal: opts?.signal,
  };
}

export const progressEndpoints = {
  /** GET /api/progress · always-current full state · ETag for client cache. */
  async get(opts: Pick<ApiFetchOptions, 'signal'> = {}): Promise<Progress> {
    const res = await apiFetch(ProgressResponse, '/api/progress', { signal: opts.signal });
    return res.progress;
  },

  async patchLast(
    body: Partial<ProgressLast>,
    opts?: PatchOpts,
  ): Promise<{ last: ProgressLast; etag: string }> {
    return apiFetch(LastEcho, '/api/progress/last', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchTicket(
    ticketId: string,
    body: Partial<TicketProgress>,
    opts?: PatchOpts,
  ): Promise<{ ticketId: string; progress: TicketProgress; etag: string }> {
    return apiFetch(TicketEcho, `/api/progress/ticket/${encodeURIComponent(ticketId)}`, {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchSprint(
    sprintId: string,
    body: Partial<SprintProgress>,
    opts?: PatchOpts,
  ): Promise<{ sprintId: string; progress: SprintProgress; etag: string }> {
    return apiFetch(SprintEcho, `/api/progress/sprint/${encodeURIComponent(sprintId)}`, {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchHomework(
    hwId: string,
    body: Partial<HomeworkProgress>,
    opts?: PatchOpts,
  ): Promise<{ hwId: string; progress: HomeworkProgress; etag: string }> {
    return apiFetch(HomeworkEcho, `/api/progress/homework/${encodeURIComponent(hwId)}`, {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchOnboarding(
    body: Partial<OnboardingFlags>,
    opts?: PatchOpts,
  ): Promise<{ onboarding: OnboardingFlags; etag: string }> {
    return apiFetch(OnboardingEcho, '/api/progress/onboarding', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchPomodoro(
    body: Partial<PomodoroState>,
    opts?: PatchOpts,
  ): Promise<{ pomodoro: PomodoroState; etag: string }> {
    return apiFetch(PomodoroEcho, '/api/progress/pomodoro', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchCockpit(
    body: Partial<CockpitState>,
    opts?: PatchOpts,
  ): Promise<{ cockpit: CockpitState; etag: string }> {
    return apiFetch(CockpitEcho, '/api/progress/cockpit', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchFlashcard(
    termId: string,
    body: Partial<FlashcardState>,
    opts?: PatchOpts,
  ): Promise<{ termId: string; flashcard: FlashcardState; etag: string }> {
    return apiFetch(
      FlashcardEcho,
      `/api/progress/cockpit/flashcards/${encodeURIComponent(termId)}`,
      {
        method: 'PATCH',
        body,
        ...withBranch(opts),
      },
    );
  },

  async patchAchievements(
    body: Partial<AchievementsState>,
    opts?: PatchOpts,
  ): Promise<{ achievements: AchievementsState; etag: string }> {
    return apiFetch(AchievementsEcho, '/api/progress/achievements', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async patchCapstoneBlock1(
    body: Partial<CapstoneState>,
    opts?: PatchOpts,
  ): Promise<{ capstone: { 'block-1'?: CapstoneState }; etag: string }> {
    return apiFetch(CapstoneEcho, '/api/progress/capstone/block-1', {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  /**
   * Generic per-block capstone sync · PATCH /api/progress/capstone/:blockId
   *
   * W3.1 Atelier extension added block-2/3/4 к CapstoneSchema · этот wrapper
   * unblocks Block 2/3/4 capstone hub clients. Validates `blockId` against
   * CapstoneBlockIdSchema enum (block-1..4) · preserves sibling blocks unlike
   * legacy `patchCapstoneBlock1`.
   *
   * Returns echo с `blockId` + `capstone` (single CapstoneState · not the
   * whole record).
   */
  async patchCapstone(
    blockId: CapstoneBlockId,
    body: Partial<CapstoneState>,
    opts?: PatchOpts,
  ): Promise<{ blockId: string; capstone: CapstoneState; etag: string }> {
    return apiFetch(CapstoneGenericEcho, `/api/progress/capstone/${encodeURIComponent(blockId)}`, {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  async getSkills(
    opts: Pick<ApiFetchOptions, 'signal'> = {},
  ): Promise<Record<string, SkillProgress>> {
    const res = await apiFetch(SkillsResponse, '/api/progress/skills', { signal: opts.signal });
    return res.skills as Record<string, SkillProgress>;
  },

  async getCtflReadiness(
    opts: Pick<ApiFetchOptions, 'signal'> = {},
  ): Promise<Record<string, number>> {
    const res = await apiFetch(ChaptersResponse, '/api/progress/ctfl-readiness', {
      signal: opts.signal,
    });
    return res.chapters;
  },

  /**
   * QuizAggregationWidget Tier 3 sync · PATCH /api/progress/quiz/:bankId
   *
   * Per-bank quiz review state (attempts · lastAttemptAt · reviewUnlockedAt ·
   * bestScore) driving Hybrid gate state machine. Multi-device sync of Tier 3
   * unlock state. Same per-id merge pattern as patchTicket/patchSprint.
   *
   * Schema landed @ QUIZ_AGGREGATION_WIDGET slot 12 MAY · endpoint 17 MAY.
   * Bank ids: `S01`-`S18` for sprint banks · `ctfl-mock-1`/`ctfl-mock-2` для mocks.
   */
  async patchQuiz(
    bankId: string,
    body: Partial<QuizProgress>,
    opts?: PatchOpts,
  ): Promise<{ bankId: string; progress: QuizProgress; etag: string }> {
    return apiFetch(QuizEcho, `/api/progress/quiz/${encodeURIComponent(bankId)}`, {
      method: 'PATCH',
      body,
      ...withBranch(opts),
    });
  },

  /**
   * CHAT_2B Q-2 · PATCH /api/progress/events
   *
   * Append client-emitted events (status changes · comments · time-spent · etc.)
   * к user's events stream. Server dedupes by `event.id` (idempotent retry-safe)
   * + prunes к 90-day window per ADR-009. Returns authoritative merged list.
   *
   * Batch ceiling 500 events per request (safety). Typical client cadence:
   * batch + flush на window blur · periodic 30s tick · or post-mutation immediate.
   *
   * Consumer: ProgressContext `addProgressEvent` setter (pending Phase H land)
   * batches events locally + invokes this wrapper. Activity endpoint (CF-D-1
   * `getActivity`) derives from synced events stream.
   */
  async patchEvents(
    events: ProgressEvent[],
    opts?: PatchOpts,
  ): Promise<{ events: ProgressEvent[]; etag: string }> {
    return apiFetch(EventsEcho, '/api/progress/events', {
      method: 'PATCH',
      body: { events },
      ...withBranch(opts),
    });
  },

  /**
   * CF-D-1 · GET /api/progress/activity?weeks=N
   *
   * Returns ISO-week-bucketed counts of tickets transitioned к `done`
   * (derived from `progress.events[]` Q-2 stream with `tickets[].completedAt`
   * fallback). Up к 52 weeks. Server clamps out-of-range к [1, 52].
   *
   * Consumer: Progress.tsx activity heatmap. Replaces inline fetch +
   * graceful-degrade fallback к client-side derivation (which remains as
   * defense if endpoint 404 / network error).
   */
  async getActivity(
    params: { weeks?: number } = {},
    opts: Pick<ApiFetchOptions, 'signal'> = {},
  ): Promise<ActivityWeek[]> {
    const weeks = params.weeks ?? 10;
    const res = await apiFetch(
      ActivityWeeksResponseSchema,
      `/api/progress/activity?weeks=${encodeURIComponent(weeks)}`,
      { signal: opts.signal },
    );
    return res.weeks;
  },
};
